import * as isomorphicLocalStorage from 'lib/storage/isomorphicLocalStorage'

const LOYALTY_ACTION_REWARD_CELEBRATION_STORAGE_KEY = 'loyaltyActionRewards_lastSeenSummary'

interface LoyaltyActionRewardAppliedCountSummaryItem {
  actionName: string
  appliedCount: number
}

function getStorageKey(memberId: string): string {
  return `${LOYALTY_ACTION_REWARD_CELEBRATION_STORAGE_KEY}_${memberId}`
}

function isAppliedCountSummaryItem(item: unknown): item is LoyaltyActionRewardAppliedCountSummaryItem {
  return typeof item === 'object'
    && item != null
    && typeof (item as LoyaltyActionRewardAppliedCountSummaryItem).actionName === 'string'
    && typeof (item as LoyaltyActionRewardAppliedCountSummaryItem).appliedCount === 'number'
}

/** Build summary type from rewards */
export function getLoyaltyActionRewardAppliedCountSummary(
  rewards: Array<App.LoyaltyActionReward>,
): Array<LoyaltyActionRewardAppliedCountSummaryItem> {
  return rewards.map(reward => ({
    actionName: reward.actionName,
    appliedCount: reward.appliedRecords.length,
  }))
}

/** Read summary type from storage */
export function readLoyaltyActionRewardCelebrationSummary(
  memberId: string,
): Array<LoyaltyActionRewardAppliedCountSummaryItem> | undefined {
  const parsed = isomorphicLocalStorage.get(getStorageKey(memberId)) as unknown
  if (!Array.isArray(parsed) || !parsed.every(isAppliedCountSummaryItem)) {
    return undefined
  }

  return parsed
}

export function writeLoyaltyActionRewardCelebrationSummary(
  memberId: string,
  summary: Array<LoyaltyActionRewardAppliedCountSummaryItem>,
): void {
  isomorphicLocalStorage.set(getStorageKey(memberId), summary)
}

export function seedLoyaltyActionRewardCelebrationSummaryIfAbsent(
  memberId: string,
  rewards: Array<App.LoyaltyActionReward>,
): boolean {
  if (readLoyaltyActionRewardCelebrationSummary(memberId)) {
    return false
  }

  writeLoyaltyActionRewardCelebrationSummary(
    memberId,
    getLoyaltyActionRewardAppliedCountSummary(rewards),
  )
  return true
}

/** Determine if any action reward completed count has increased since last seen/saved */
export function getAdvancedLoyaltyActionRewardNames(
  previous: Array<LoyaltyActionRewardAppliedCountSummaryItem>,
  current: Array<LoyaltyActionRewardAppliedCountSummaryItem>,
): Array<string> {
  const previousAppliedCountByActionName = new Map(previous.map(item => [item.actionName, item.appliedCount]))

  return current
    // Absent from previous (newly eligible) is treated as 0 so a first apply still celebrates.
    .filter(item => item.appliedCount > (previousAppliedCountByActionName.get(item.actionName) ?? 0))
    .map(item => item.actionName)
}
